import axios from "axios";
import { useEffect, useState } from "react";

import { sanitizeLessonContent } from "../../../shared/utils/commonUtils";
import { getContentItem } from "../api/learnApi";
import type { ContentKind, LessonContent } from "../lessonTypes";
import { LESSON_MESSAGES } from "../messages";
import type { ProjectContent } from "../projectTypes";

// Unknown types from the server become null, which the page shows as "unsupported"
const toContentKind = (type?: string): ContentKind | null =>
    type === "lesson" || type === "project" || type === "video" ? type : null;

// Fetches one content item (lesson / project / video). Lesson content is sanitized first.
export const useCourseContent = (courseId: string, contentId: string, language: string) => {
    const [content, setContent] = useState<LessonContent | ProjectContent | null>(null);
    const [contentType, setContentType] = useState<ContentKind | null>(null);
    // The learner already finished this content (server: course_level >= content.level)
    const [alreadyCompleted, setAlreadyCompleted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!courseId || !contentId) {
            setLoading(false);
            setError(LESSON_MESSAGES.contentFailed);
            return;
        }

        const controller = new AbortController();

        // Clear the previous item so it doesn't flash while the next one loads
        setContent(null);
        setContentType(null);
        setError("");
        setLoading(true);

        getContentItem(courseId, contentId, language, controller.signal)
            .then((item) => {
                const type = toContentKind(item.content.type);

                setContentType(type);
                setAlreadyCompleted(item.alreadyCompleted);
                setContent(
                    type === "lesson"
                        ? (sanitizeLessonContent(item.content) as LessonContent)
                        : (item.content as unknown as ProjectContent)
                );
            })
            .catch((err) => {
                if (axios.isCancel(err)) return; // Params changed or the page was left
                console.error("Content fetch failed:", err);
                setError(LESSON_MESSAGES.contentFailed);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [courseId, contentId, language]);

    return { content, contentType, alreadyCompleted, loading, error };
};